/**
 * /admin/activity — consolidated audit log across every app + user + admin
 * action. Same renderer as the per-app Activity page, with the app slug
 * shown inline when relevant.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api/client.js';
import type { AdminActivityRow } from '../../api/types.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import {
  IconActivity,
  IconBolt,
  IconGlobe,
  IconKey,
  IconLock,
  IconPlus,
  IconRefresh,
  IconRocket,
  IconShield,
  IconTrash,
  IconUsers,
} from '../../components/Icons.js';
import { Banner, Button, EmptyState, PageHeader, PageSkeleton } from '../../components/ui.js';
import { relTime } from '../../lib/format.js';

interface State {
  loading: boolean;
  rows?: AdminActivityRow[];
  hasMore?: boolean;
  error?: string;
}

interface Descriptor {
  text: string;
  icon: typeof IconBolt;
  tone: 'brand' | 'muted' | 'warn' | 'danger' | 'ok';
}

const TONE_RING: Record<Descriptor['tone'], string> = {
  brand: 'bg-brand-soft text-brand',
  muted: 'bg-surface-2 text-muted',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  ok: 'bg-ok-soft text-ok',
};

function actorLabel(row: AdminActivityRow): string {
  return row.actor_name ?? row.actor_email ?? 'System';
}

function describe(row: AdminActivityRow): Descriptor {
  let meta: Record<string, unknown> = {};
  if (row.metadata) {
    try {
      meta = JSON.parse(row.metadata) as Record<string, unknown>;
    } catch {
      // ignore
    }
  }
  switch (row.action) {
    case 'app.create':
      return { text: 'created the app', icon: IconRocket, tone: 'brand' };
    case 'app.update':
      return { text: "updated the app's metadata", icon: IconBolt, tone: 'muted' };
    case 'app.delete': {
      // The apps row is gone after this fires — the only place the
      // human-readable identity survives is metadata.name (or metadata.slug
      // as a fallback for legacy rows that didn't capture name).
      const id =
        (meta['name'] as string | undefined) ?? (meta['slug'] as string | undefined) ?? 'the app';
      return { text: `deleted ${id}`, icon: IconTrash, tone: 'danger' };
    }
    case 'app.admin_delete': {
      const id =
        (meta['name'] as string | undefined) ?? (meta['slug'] as string | undefined) ?? 'the app';
      return { text: `admin-deleted ${id}`, icon: IconTrash, tone: 'danger' };
    }
    case 'app.disable':
      return { text: 'disabled the app', icon: IconBolt, tone: 'warn' };
    case 'app.enable':
      return { text: 're-enabled the app', icon: IconBolt, tone: 'ok' };
    case 'app.visibility':
      return meta['to'] === 'workspace'
        ? { text: 'made the app visible to everyone at Mytra', icon: IconGlobe, tone: 'brand' }
        : { text: 'made the app private', icon: IconLock, tone: 'muted' };
    case 'role.grant': {
      const email = (meta['target_email'] as string | undefined) ?? '?';
      const role = (meta['role'] as string | undefined) ?? '?';
      return { text: `granted ${email} the ${role} role`, icon: IconUsers, tone: 'brand' };
    }
    case 'role.revoke': {
      const email = (meta['target_email'] as string | undefined) ?? '?';
      return { text: `revoked ${email}'s role`, icon: IconUsers, tone: 'danger' };
    }
    case 'admin.self_grant': {
      const role = (meta['role'] as string | undefined) ?? '?';
      return { text: `granted themselves the ${role} role`, icon: IconShield, tone: 'warn' };
    }
    case 'secret.create':
      return { text: `added secret ${meta['name'] ?? '?'}`, icon: IconKey, tone: 'brand' };
    case 'secret.rotate':
      return { text: `rotated secret ${meta['name'] ?? '?'}`, icon: IconRefresh, tone: 'ok' };
    case 'secret.delete':
      return { text: `deleted secret ${meta['name'] ?? '?'}`, icon: IconKey, tone: 'danger' };
    case 'dependency.add':
      return {
        text: `wired ${meta['caller_slug'] ?? '?'} → ${meta['target_slug'] ?? '?'}`,
        icon: IconPlus,
        tone: 'brand',
      };
    case 'dependency.remove':
      return {
        text: `disconnected ${meta['caller_slug'] ?? '?'} from ${meta['target_slug'] ?? '?'}`,
        icon: IconTrash,
        tone: 'danger',
      };
    case 'user.first_seen':
      return { text: 'signed in for the first time', icon: IconUsers, tone: 'brand' };
    case 'user.global_role_changed': {
      const role = (meta['new_role'] as string | undefined) ?? '?';
      const email = (meta['email'] as string | undefined) ?? '?';
      return {
        text: `set ${email}'s role to ${role}`,
        icon: IconShield,
        tone: role === 'platform_admin' ? 'brand' : 'muted',
      };
    }
    default:
      return { text: row.action, icon: IconBolt, tone: 'muted' };
  }
}

export function AdminActivity() {
  const [state, setState] = useState<State>({ loading: true });

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const res = await api.get<{ activity: AdminActivityRow[]; hasMore: boolean }>(
        '/api/admin/activity?limit=100',
      );
      setState({ loading: false, rows: res.activity, hasMore: res.hasMore });
    } catch (err) {
      setState({
        loading: false,
        error: err instanceof ApiError ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore() {
    if (!state.rows || !state.hasMore) return;
    const last = state.rows[state.rows.length - 1];
    if (!last) return;
    try {
      const res = await api.get<{ activity: AdminActivityRow[]; hasMore: boolean }>(
        `/api/admin/activity?limit=100&before=${last.created_at}`,
      );
      setState((prev) =>
        prev.rows ? { ...prev, rows: [...prev.rows, ...res.activity], hasMore: res.hasMore } : prev,
      );
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof ApiError ? err.message : String(err),
      }));
    }
  }

  if (state.loading) return <PageSkeleton rows={6} />;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="Audit log"
        description="Every action across every app and user, newest first."
      />

      <AdminTabs />

      {state.error && <Banner tone="error">{state.error}</Banner>}

      {state.rows && state.rows.length === 0 ? (
        <EmptyState
          icon={<IconActivity size={18} />}
          title="No activity yet"
          description="As people deploy, change access, or rotate secrets, you'll see it here."
        />
      ) : (
        <ol className="relative space-y-3 border-l-2 border-line ml-3 pl-6">
          {(state.rows ?? []).map((row) => {
            const d = describe(row);
            const Icon = d.icon;
            // After a hard-delete the app is gone — render the slug chip as
            // plain text instead of a link to a 404. The describe() text
            // already inlines the human-readable name from metadata.
            const isAppGone = row.action === 'app.delete' || row.action === 'app.admin_delete';
            return (
              <li key={row.id} className="relative">
                <span
                  className={`absolute -left-[34px] top-1.5 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-bg ${TONE_RING[d.tone]}`}
                  aria-hidden
                >
                  <Icon size={12} />
                </span>
                <div className="rounded-lg border border-line bg-surface px-4 py-3 text-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="min-w-0">
                      <span className="font-medium text-ink">{actorLabel(row)}</span>{' '}
                      <span className="text-muted">{d.text}</span>
                      {row.app_slug &&
                        (isAppGone ? (
                          <span className="text-xs text-faint ml-2 font-mono line-through">
                            {row.app_slug}
                          </span>
                        ) : (
                          <Link
                            to={`/admin/apps/${row.app_slug}`}
                            className="text-xs text-brand hover:underline ml-2"
                          >
                            {row.app_slug}
                          </Link>
                        ))}
                    </span>
                    <span className="text-xs text-faint shrink-0">{relTime(row.created_at)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {state.hasMore && (
        <div className="text-center mt-5">
          <Button variant="secondary" size="sm" onClick={() => void loadMore()}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
